import { __ } from "@wordpress/i18n"
import { 
  useBlockProps, 
  InspectorControls,
  RichText,
  MediaUpload,
  MediaUploadCheck
} from "@wordpress/block-editor"
import { 
  PanelBody,
  Notice,
  Placeholder,
  Spinner,
  Button
} from "@wordpress/components"
import ServerSideRender from "@wordpress/server-side-render"
import { useState } from "@wordpress/element"

export default function Edit({ attributes, setAttributes, clientId }) {
  const blockProps = useBlockProps({
    className: `hero-section ${attributes.align ? `align${attributes.align}` : ''}`
  })

  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [isEditingSubtitle, setIsEditingSubtitle] = useState(false)

  // If we're editing either field, show the rich text editors instead of ServerSideRender
  if (isEditingTitle || isEditingSubtitle) {
    return (
      <>
        <InspectorControls>
          <PanelBody title={__('Hero Section Settings', 'puroclean-tw')} initialOpen={true}>
            <Notice status="info" isDismissible={false}>
              <p>
                <strong>Title and Subtitle:</strong> Click "Done Editing" to see full preview<br />
                <strong>Phone & Location:</strong> Managed via Site Globals<br />
                <strong>Images:</strong> Configured in theme settings
              </p>
            </Notice>
          </PanelBody>
          
          {/* Hero Images Panel */}
          <PanelBody title={__('Hero Images', 'puroclean-tw')} initialOpen={false}>
            {/* Desktop Image */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                {__('Desktop Hero Image:', 'puroclean-tw')}
              </label>
              <MediaUploadCheck>
                <MediaUpload
                  onSelect={(media) => setAttributes({ 
                    heroImageDesktop: {
                      url: media.url,
                      id: media.id,
                      alt: media.alt || 'Restoration Workers'
                    }
                  })}
                  allowedTypes={['image']}
                  value={attributes.heroImageDesktop.id}
                  render={({ open }) => (
                    <div>
                      {attributes.heroImageDesktop.url ? (
                        <div>
                          <img 
                            src={attributes.heroImageDesktop.url} 
                            alt={attributes.heroImageDesktop.alt}
                            style={{ maxWidth: '200px', marginBottom: '10px', display: 'block' }}
                          />
                          <Button onClick={open} variant="secondary" style={{ marginRight: '10px' }}>
                            {__('Change Image', 'puroclean-tw')}
                          </Button>
                          <Button 
                            onClick={() => setAttributes({ 
                              heroImageDesktop: { url: '', id: 0, alt: 'Restoration Workers' }
                            })} 
                            variant="link" 
                            isDestructive
                          >
                            {__('Remove (Use Default)', 'puroclean-tw')}
                          </Button>
                        </div>
                      ) : (
                        <Button onClick={open} variant="secondary">
                          {__('Select Desktop Image', 'puroclean-tw')}
                        </Button>
                      )}
                    </div>
                  )}
                />
              </MediaUploadCheck>
              <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                {__('If no image is selected, the default theme image will be used.', 'puroclean-tw')}
              </p>
            </div>
            
            {/* Mobile Image */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                {__('Mobile Hero Image:', 'puroclean-tw')}
              </label>
              <MediaUploadCheck>
                <MediaUpload
                  onSelect={(media) => setAttributes({ 
                    heroImageMobile: {
                      url: media.url,
                      id: media.id,
                      alt: media.alt || 'Restoration Workers'
                    }
                  })}
                  allowedTypes={['image']}
                  value={attributes.heroImageMobile.id}
                  render={({ open }) => (
                    <div>
                      {attributes.heroImageMobile.url ? (
                        <div>
                          <img 
                            src={attributes.heroImageMobile.url} 
                            alt={attributes.heroImageMobile.alt}
                            style={{ maxWidth: '200px', marginBottom: '10px', display: 'block' }}
                          />
                          <Button onClick={open} variant="secondary" style={{ marginRight: '10px' }}>
                            {__('Change Image', 'puroclean-tw')}
                          </Button>
                          <Button 
                            onClick={() => setAttributes({ 
                              heroImageMobile: { url: '', id: 0, alt: 'Restoration Workers' }
                            })} 
                            variant="link" 
                            isDestructive
                          >
                            {__('Remove (Use Default)', 'puroclean-tw')}
                          </Button>
                        </div>
                      ) : (
                        <Button onClick={open} variant="secondary">
                          {__('Select Mobile Image', 'puroclean-tw')}
                        </Button>
                      )}
                    </div>
                  )}
                />
              </MediaUploadCheck>
              <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                {__('If no image is selected, the default theme image will be used.', 'puroclean-tw')}
              </p>
            </div>
          </PanelBody>
        </InspectorControls>

        <div {...blockProps}>
          <Placeholder 
            icon="edit" 
            label={__('Hero Section - Edit Mode', 'puroclean-tw')}
            instructions={__('Edit your title and subtitle below. Click "Done Editing" to see the full preview.', 'puroclean-tw')}
          >
            <div style={{ width: '100%', marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                {__('Hero Title:', 'puroclean-tw')}
              </label>
              <RichText
                tagName="div"
                placeholder={__('Enter hero title...', 'puroclean-tw')}
                value={attributes.heroTitle}
                onChange={(value) => setAttributes({ heroTitle: value })}
                style={{ 
                  border: '1px solid #ddd', 
                  padding: '10px', 
                  minHeight: '50px',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}
              />
            </div>
            
            <div style={{ width: '100%', marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                {__('Hero Subtitle:', 'puroclean-tw')}
              </label>
              <RichText
                tagName="div"
                placeholder={__('Enter hero subtitle...', 'puroclean-tw')}
                value={attributes.heroSubtitle}
                onChange={(value) => setAttributes({ heroSubtitle: value })}
                style={{ 
                  border: '1px solid #ddd', 
                  padding: '10px', 
                  minHeight: '40px',
                  fontSize: '16px',
                  color: '#0073aa'
                }}
              />
            </div>
            
            <button 
              className="button button-primary"
              onClick={() => {
                setIsEditingTitle(false)
                setIsEditingSubtitle(false)
              }}
            >
              {__('Done Editing', 'puroclean-tw')}
            </button>
          </Placeholder>
        </div>
      </>
    )
  }

  return (
    <>
      <InspectorControls>
        <PanelBody title={__('Hero Section Settings', 'puroclean-tw')} initialOpen={true}>
          <Notice status="info" isDismissible={false}>
            <p>
              <strong>Title and Subtitle:</strong> Click "Edit Content" button to modify<br />
              <strong>Phone & Location:</strong> Managed via Site Globals<br />
              <strong>Preview:</strong> Shows exact frontend appearance
            </p>
          </Notice>
          
          <div style={{ marginTop: '15px' }}>
            <button 
              className="button button-secondary"
              onClick={() => {
                setIsEditingTitle(true)
                setIsEditingSubtitle(true)
              }}
              style={{ width: '100%' }}
            >
              {__('Edit Title & Subtitle', 'puroclean-tw')}
            </button>
          </div>
        </PanelBody>
        
        {/* Hero Images Panel */}
        <PanelBody title={__('Hero Images', 'puroclean-tw')} initialOpen={false}>
          {/* Desktop Image */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
              {__('Desktop Hero Image:', 'puroclean-tw')}
            </label>
            <MediaUploadCheck>
              <MediaUpload
                onSelect={(media) => setAttributes({ 
                  heroImageDesktop: {
                    url: media.url,
                    id: media.id,
                    alt: media.alt || 'Restoration Workers'
                  }
                })}
                allowedTypes={['image']}
                value={attributes.heroImageDesktop.id}
                render={({ open }) => (
                  <div>
                    {attributes.heroImageDesktop.url ? (
                      <div>
                        <img 
                          src={attributes.heroImageDesktop.url} 
                          alt={attributes.heroImageDesktop.alt}
                          style={{ maxWidth: '200px', marginBottom: '10px', display: 'block' }}
                        />
                        <Button onClick={open} variant="secondary" style={{ marginRight: '10px' }}>
                          {__('Change Image', 'puroclean-tw')}
                        </Button>
                        <Button 
                          onClick={() => setAttributes({ 
                            heroImageDesktop: { url: '', id: 0, alt: 'Restoration Workers' }
                          })} 
                          variant="link" 
                          isDestructive
                        >
                          {__('Remove (Use Default)', 'puroclean-tw')}
                        </Button>
                      </div>
                    ) : (
                      <Button onClick={open} variant="secondary">
                        {__('Select Desktop Image', 'puroclean-tw')}
                      </Button>
                    )}
                  </div>
                )}
              />
            </MediaUploadCheck>
            <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
              {__('If no image is selected, the default theme image will be used.', 'puroclean-tw')}
            </p>
          </div>
          
          {/* Mobile Image */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
              {__('Mobile Hero Image:', 'puroclean-tw')}
            </label>
            <MediaUploadCheck>
              <MediaUpload
                onSelect={(media) => setAttributes({ 
                  heroImageMobile: {
                    url: media.url,
                    id: media.id,
                    alt: media.alt || 'Restoration Workers'
                  }
                })}
                allowedTypes={['image']}
                value={attributes.heroImageMobile.id}
                render={({ open }) => (
                  <div>
                    {attributes.heroImageMobile.url ? (
                      <div>
                        <img 
                          src={attributes.heroImageMobile.url} 
                          alt={attributes.heroImageMobile.alt}
                          style={{ maxWidth: '200px', marginBottom: '10px', display: 'block' }}
                        />
                        <Button onClick={open} variant="secondary" style={{ marginRight: '10px' }}>
                          {__('Change Image', 'puroclean-tw')}
                        </Button>
                        <Button 
                          onClick={() => setAttributes({ 
                            heroImageMobile: { url: '', id: 0, alt: 'Restoration Workers' }
                          })} 
                          variant="link" 
                          isDestructive
                        >
                          {__('Remove (Use Default)', 'puroclean-tw')}
                        </Button>
                      </div>
                    ) : (
                      <Button onClick={open} variant="secondary">
                        {__('Select Mobile Image', 'puroclean-tw')}
                      </Button>
                    )}
                  </div>
                )}
              />
            </MediaUploadCheck>
            <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
              {__('If no image is selected, the default theme image will be used.', 'puroclean-tw')}
            </p>
          </div>
        </PanelBody>
      </InspectorControls>

      <div {...blockProps}>
        <ServerSideRender
          block="puroclean/hero-section"
          attributes={attributes}
          LoadingResponsePlaceholder={() => (
            <Placeholder>
              <Spinner />
              <span>{__('Loading hero section preview...', 'puroclean-tw')}</span>
            </Placeholder>
          )}
          ErrorResponsePlaceholder={({ response }) => (
            <Placeholder 
              icon="warning" 
              label={__('Hero Section Preview Error', 'puroclean-tw')}
            >
              <p>{__('There was an error loading the preview. The block will still work on the frontend.', 'puroclean-tw')}</p>
              {response && (
                <details style={{ marginTop: '10px' }}>
                  <summary>Technical Details</summary>
                  <pre style={{ fontSize: '12px', background: '#f0f0f0', padding: '10px', marginTop: '5px' }}>
                    {JSON.stringify(response, null, 2)}
                  </pre>
                </details>
              )}
            </Placeholder>
          )}
          EmptyResponsePlaceholder={() => (
            <Placeholder 
              icon="block-default" 
              label={__('Hero Section', 'puroclean-tw')}
              instructions={__('This block is ready to display. If you see this message, the render.php file may be empty.', 'puroclean-tw')}
            />
          )}
        />
        
        {/* Quick edit overlay */}
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          background: 'rgba(0, 0, 0, 0.8)',
          color: 'white',
          padding: '5px 10px',
          borderRadius: '4px',
          fontSize: '12px',
          cursor: 'pointer',
          zIndex: 10
        }}
        onClick={() => {
          setIsEditingTitle(true)
          setIsEditingSubtitle(true)
        }}
        title={__('Click to edit title and subtitle', 'puroclean-tw')}
        >
          ✏️ {__('Edit Content', 'puroclean-tw')}
        </div>
      </div>
    </>
  )
} 